<template>
  <div class="s-columnVisibilityModal">
    <KButton variant="text" class="s-columnVisibilityModal__trigger" :disabled="disabled" @click="openDialog">
      <span v-if="hasChangesFromDefault" class="s-columnVisibilityModal__indicator" />
      <VIcon>far fa-cog</VIcon>
    </KButton>

    <v-dialog v-model="isOpen" max-width="500">
      <v-card class="s-columnVisibilityModal__dialog">
        <div class="s-columnVisibilityModal__header">
          <div class="text-h2">{{ $t("global.columnVisibility.title") }}</div>
        </div>

        <div class="s-columnVisibilityModal__content">
          <p class="text-body-2 mb-5">{{ $t("global.columnVisibility.description") }}</p>

          <div class="s-columnVisibilityModal__list">
            <div v-for="column in draftColumns" :key="column.key" class="s-columnVisibilityModal__row">
              <KSwitch :model-value="column.visible" :disabled="column.alwaysVisible" hide-details :label="column.label" @update:model-value="toggleColumn(column.key)" />
            </div>
          </div>

          <button type="button" class="s-columnVisibilityModal__reset" :disabled="isResetDisabled" @click="resetToDefaults">
            {{ $t("global.columnVisibility.reset") }}
          </button>

          <KAlert v-if="showAllColumnsHiddenWarning" type="warning" class="mt-4">
            {{ $t("global.columnVisibility.allColumnsHiddenWarning") }}
          </KAlert>
        </div>

        <div class="s-columnVisibilityModal__actions">
          <KButton primary :disabled="disabled || showAllColumnsHiddenWarning" @click="confirm">
            {{ $t("global.columnVisibility.confirm") }}
          </KButton>
          <KButton @click="isOpen = false">
            {{ $t("global.columnVisibility.cancel") }}
          </KButton>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"

import KAlert from "@/components/KAlert.vue"
import KButton from "@/components/KButton.vue"
import KSwitch from "@/components/KSwitch.vue"
import type { ColumnVisibilityPanelEntry } from "@/components/KTable/columnVisibility"

const props = defineProps<{
  columns: ColumnVisibilityPanelEntry[]
  hasChangesFromDefault: boolean
  disabled?: boolean
}>()

const emit = defineEmits<{
  confirm: [visibleColumnKeys: string[]]
}>()

const isOpen = ref(false)
const draftVisibility = ref<Record<string, boolean>>({})

const draftColumns = computed(() => {
  return props.columns.map((column) => ({
    ...column,
    visible: draftVisibility.value[column.key] ?? column.visible,
  }))
})

const showAllColumnsHiddenWarning = computed(() => {
  return draftColumns.value.every((column) => !column.visible)
})

const isResetDisabled = computed(() => {
  return draftColumns.value.every((column) => column.visible === column.defaultVisible)
})

function syncDraftVisibility() {
  draftVisibility.value = Object.fromEntries(props.columns.map((column) => [column.key, column.visible]))
}

function openDialog() {
  syncDraftVisibility()
  isOpen.value = true
}

function toggleColumn(columnKey: string) {
  const column = props.columns.find((entry) => entry.key === columnKey)
  if (column?.alwaysVisible) {
    return
  }

  draftVisibility.value[columnKey] = !draftVisibility.value[columnKey]
}

function resetToDefaults() {
  draftVisibility.value = Object.fromEntries(props.columns.map((column) => [column.key, column.defaultVisible]))
}

function confirm() {
  if (props.disabled || showAllColumnsHiddenWarning.value) {
    return
  }

  emit(
    "confirm",
    draftColumns.value.filter((column) => column.visible).map((column) => column.key),
  )
  isOpen.value = false
}
</script>

<style scoped lang="scss">
.s-columnVisibilityModal__trigger {
  min-width: 44px;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  position: relative;
  color: $color-grey4;
}

.s-columnVisibilityModal__indicator {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: $color-tu2;
  border: 2px solid white;
}

.s-columnVisibilityModal__dialog {
  overflow: hidden;
}

.s-columnVisibilityModal__header {
  padding: 18px 20px;
  border-bottom: 1px solid $color-grey2;
  background: $color-vio1;
  color: white;
}

.s-columnVisibilityModal__content {
  padding: 24px 20px 20px 20px;
}

.s-columnVisibilityModal__list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.s-columnVisibilityModal__row {
  padding: 6px 0;
  border-bottom: 1px solid $color-grey1;
}

.s-columnVisibilityModal__reset {
  margin-top: 12px;
  border: 0;
  background: transparent;
  color: $color-tu2;
  text-decoration: underline;
  padding: 0;
  cursor: pointer;
}

.s-columnVisibilityModal__reset:disabled {
  color: $color-grey3;
  cursor: default;
}

.s-columnVisibilityModal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 20px 20px 20px;
}
</style>
